import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import useTheme from "@mui/material/styles/useTheme";
import { alpha } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import Language from "@mui/icons-material/Language";
import ShoppingCart from "@mui/icons-material/ShoppingCart";
import Timer from "@mui/icons-material/Timer";
import CheckCircle from "@mui/icons-material/CheckCircle";
import School from "@mui/icons-material/School";
import TrendingUp from "@mui/icons-material/TrendingUp";
import Update from "@mui/icons-material/Update";
import Chat from "@mui/icons-material/Chat";
import Autorenew from "@mui/icons-material/Autorenew";
import SupportAgent from "@mui/icons-material/SupportAgent";

// Custom theme
import { createTheme, ThemeProvider } from "@mui/material/styles";

export const customTheme = createTheme({
  palette: {
    primary: {
      main: "#27b1bb",
    },
    secondary: {
      main: "#ff6b6b",
    },
    background: {
      default: "#f8f9fa",
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

const FeatureCard = ({ icon, title, description }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={4}
      sx={{
        p: 4,
        height: "80%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
        "&:hover": {
          transform: "translateY(-10px)",
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <Box sx={{ mb: 2, color: "primary.main" }}>
        {React.cloneElement(icon, { style: { fontSize: 60 } })}
      </Box>
      <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );
};

const ExamModesSection = () => (
  <Box>
    <Typography
      variant="h2"
      gutterBottom
      align="center"
      fontWeight="bold"
      mb={6}
      sx={{ fontSize: "36px" }}
    >
      {/* Tailored Interactive Exam Modes for Optimal Preparation */}
      Interactive Practice Tests with Real and Practice Modes
    </Typography>
    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        <Paper
          elevation={4}
          sx={{
            p: 4,
            height: "80%",
            bgcolor: alpha(customTheme.palette.primary.main, 0.05),
          }}
        >
          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            fontWeight="bold"
            color="primary.main"
          >
            Real Mode
          </Typography>
          <List>
            {[
              { icon: <Timer />, text: "Fixed time limit" },
              { icon: <CheckCircle />, text: "Fixed number of questions" },
              { icon: <School />, text: "Simulates actual exam conditions" },
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 2 }}>
                <ListItemIcon sx={{ color: "primary.main" }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper
          elevation={4}
          sx={{
            p: 4,
            height: "80%",
            bgcolor: alpha(customTheme.palette.secondary.main, 0.05),
          }}
        >
          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            fontWeight="bold"
            color="secondary.main"
          >
            Practice Mode
          </Typography>
          <List>
            {[
              { icon: <Timer />, text: "Optional timer" },
              { icon: <CheckCircle />, text: "Customize number of questions" },
              { icon: <School />, text: "Immediate feedback and explanations" },
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 2 }}>
                <ListItemIcon sx={{ color: "secondary.main" }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

const AdditionalFeaturesSection = () => {
  const theme = useTheme();

  return (
    <Box sx={{ my: 14 }}>
      <Typography
        variant="h2"
        gutterBottom
        align="center"
        fontWeight="bold"
        mb={6}
        sx={{ fontSize: "36px", mb: 14 }}
      >
        Empowering Your Learning Journey
      </Typography>
      <Grid container spacing={4} alignItems="stretch">
        <Grid item xs={12} md={6} sx={{ mb: { xs: 3, md: 0 } }}>
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "visible",
              boxShadow: theme.shadows[10],
              transition: "transform 0.3s ease-in-out",
              "&:hover": {
                transform: "translateY(-10px)",
              },
            }}
          >
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 80,
                height: 80,
                position: "absolute",
                top: -40,
                left: "calc(50% - 40px)",
                boxShadow: theme.shadows[3],
              }}
            >
              <Autorenew sx={{ fontSize: 40 }} />
            </Avatar>
            <CardContent sx={{ pt: 6, pb: 4, px: 4 }}>
              <Typography
                variant="h5"
                component="h3"
                fontWeight="bold"
                align="center"
                mb={3}
              >
                Always Up-to-Date Syllabus
              </Typography>
              <Typography variant="body1" paragraph align="center">
                Stay ahead with our continuously updated study materials,
                reflecting the latest industry standards and exam requirements.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-around",
                  mt: 4,
                }}
              >
                {[
                  { icon: <Update />, label: "Regular Updates" },
                  { icon: <CheckCircle />, label: "Exam Aligned" },
                  { icon: <TrendingUp />, label: "Industry Trends" },
                ].map((item, index) => (
                  <Box key={index} sx={{ textAlign: "center" }}>
                    <Avatar
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        mb: 1,
                        mx: "auto",
                      }}
                    >
                      {React.cloneElement(item.icon, { color: "primary" })}
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "visible",
              boxShadow: theme.shadows[10],
              transition: "transform 0.3s ease-in-out",
              "&:hover": {
                transform: "translateY(-10px)",
              },
            }}
          >
            <Avatar
              sx={{
                bgcolor: theme.palette.secondary.main,
                width: 80,
                height: 80,
                position: "absolute",
                top: -40,
                left: "calc(50% - 40px)",
                boxShadow: theme.shadows[3],
              }}
            >
              <SupportAgent sx={{ fontSize: 40 }} />
            </Avatar>
            <CardContent sx={{ pt: 6, pb: 4, px: 4 }}>
              <Typography
                variant="h5"
                component="h3"
                fontWeight="bold"
                align="center"
                mb={3}
              >
                24/7 Expert Support
              </Typography>
              <Typography variant="body1" paragraph align="center">
                Get round-the-clock assistance from our team of certification
                experts, ensuring you're never left in doubt.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-around",
                  mt: 4,
                }}
              >
                {[
                  { icon: <Chat />, label: "Live Chat" },
                  { icon: <School />, label: "Expert Guidance" },
                  { icon: <Timer />, label: "Always Available" },
                ].map((item, index) => (
                  <Box key={index} sx={{ textAlign: "center" }}>
                    <Avatar
                      sx={{
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        mb: 1,
                        mx: "auto",
                      }}
                    >
                      {React.cloneElement(item.icon, { color: "secondary" })}
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const LandingPageContent = () => (
  <ThemeProvider theme={customTheme}>
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 4 }}>
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Typography
          variant="h2"
          gutterBottom
          fontWeight="bold"
          sx={{ fontSize: "36px" }}
        >
          {/* Master Your Certification Exam */}
          Pass Your Certification with Our 100% Guarantee
        </Typography>
        <Typography
          variant="span"
          gutterBottom
          color="text.secondary"
          sx={{ fontFamily: "Poppins" }}
        >
          Comprehensive study materials tailored to your learning style
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {[
          {
            icon: <PictureAsPdf />,
            // title: "PDF Study Guides",
            title: "PDF Study Guides (Q&A)",
            description:
              "Access comprehensive, downloadable materials for offline study and quick reference. Our guides are tailored to fit your learning style, ensuring you master every topic with ease.",
          },
          {
            icon: <Language />,
            title: "Interactive Web Based Practice Exams",
            description:
              "Experience realistic exam simulations with detailed result analytics. Track your progress and identify areas for improvement, giving you the confidence to succeed.",
          },
          {
            icon: <ShoppingCart />,
            title: "Flexible Packages",
            description:
              "Choose from a range of options to suit your needs. Whether you prefer PDF guides, web-based exams, or bundled packages, we have the perfect solution to help you achieve your certification goals.",
          },
        ].map((feature, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <FeatureCard {...feature} />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 14 }} />

      <ExamModesSection />
      <AdditionalFeaturesSection />
      {/* <StatisticsSection /> */}
    </Box>
  </ThemeProvider>
);

export default LandingPageContent;
